import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../../utils/toast";
import { AlertCircle, ExternalLink } from "lucide-react";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { FieldLabel } from "../../../../components/ui/FieldLabel";
import { enrollShopifyDirect } from "../../../../api/integrations";
import { apiBodyMessage } from "../../../../utils/apiErrorMessage";
import { iconList } from "../../../../utils/iconList";
import { cn } from "../../../../utils/cn";
import "../ModernIntegrations.scss";

const normalizeDomain = (value) =>
  value.trim().replace(/^https?:\/\//i, "").replace(/\/+$/, "").split(/[/?#]/)[0];

// Mirrors SHOP_DOMAIN_REGEX in the backend (microservice-integrations,
// shopify-connect.service.ts): the store host, already lowercased. Rejecting it
// here keeps the user from sending a request the server answers with a 400.
const SHOP_DOMAIN_REGEX = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;

/**
 * Vinculación de una tienda Shopify por credenciales directas.
 *
 * El comercio crea una app en SU propio Dev Dashboard, la instala en su tienda
 * y pega acá `client_id` y `client_secret`. Con eso el backend acuña los tokens
 * y registra los webhooks sin volver a molestarlo.
 *
 * Es la vía puente mientras la app pública de Beartrack está en revisión. No
 * requiere que el comercio sea Shopify Partner ni que su tienda esté en un
 * plan determinado.
 *
 * El `client_secret` se manda una sola vez, se cifra en el backend y no vuelve
 * nunca en ninguna respuesta: por eso el campo se limpia al cerrar y no se
 * pre-rellena al reabrir.
 */
/**
 * Permisos que el comercio le tiene que dar a SU app de Shopify.
 *
 * Son dos familias distintas y hacen falta las dos, porque Shopify no las
 * unificó:
 *
 * - Los `*_merchant_managed_fulfillment_orders` son los de la API moderna
 *   (FulfillmentOrder). Habilitan crear el despacho con su número de
 *   seguimiento, que es lo que hace que el pedido figure enviado.
 * - `write_fulfillments` es el scope LEGACY de la Fulfillment API. Parece de
 *   más, pero `fulfillmentEventCreate` —la única operación que escribe
 *   "Entregado" en Shopify— nunca migró y lo sigue exigiendo.
 *
 * - `read_products` habilita leer el id de producto y de variante de cada
 *   ítem. El nombre, el SKU, la cantidad, el precio, el peso y la imagen SÍ
 *   llegan sin él —cuelgan del propio line item—, así que su ausencia no deja
 *   al pedido sin productos: lo deja sin forma de vincularlos al catálogo.
 *
 * Medido en producción el 2026-08-17: sin `write_fulfillments`, Shopify
 * responde `Access denied for fulfillmentEventCreate field`, los pedidos
 * quedan para siempre en "Fulfilled" y el comprador nunca ve la entrega.
 * Pedirlo desde el alta evita tener que hacer reinstalar al comercio después:
 * cambiar los permisos de una app ya instalada exige publicar una versión
 * nueva Y volver a autorizarla en la tienda.
 */
const SHOPIFY_SCOPES = [
  "read_orders",
  "read_merchant_managed_fulfillment_orders",
  "write_merchant_managed_fulfillment_orders",
  "write_fulfillments",
  "read_products",
];

export default function AddShopifyModal({ open, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [domain, setDomain] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const domainId = useId();
  const clientIdId = useId();
  const clientSecretId = useId();
  const errorId = useId();
  const domainRef = useRef(null);

  // Foco inicial en el primer campo (el Modal enfoca su botón de cerrar).
  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => domainRef.current?.focus(), 60);
    return () => clearTimeout(id);
  }, [open]);

  // Al cerrar se descarta TODO, incluido el secreto: no debe quedar en memoria
  // del componente esperando una reapertura.
  useEffect(() => {
    if (open) return;
    setDomain("");
    setClientId("");
    setClientSecret("");
    setErrorMessage("");
  }, [open]);

  const cleanDomain = normalizeDomain(domain);
  const canSubmit =
    Boolean(cleanDomain) && Boolean(clientId.trim()) && Boolean(clientSecret.trim());

  const handleSubmit = async () => {
    if (saving) return;

    const shopDomain = cleanDomain.toLowerCase();

    if (!shopDomain.endsWith(".myshopify.com")) {
      setErrorMessage(
        t("El dominio debe terminar en .myshopify.com — por ejemplo: mi-tienda.myshopify.com"),
      );
      domainRef.current?.focus();
      return;
    }

    if (!SHOP_DOMAIN_REGEX.test(shopDomain)) {
      setErrorMessage(
        t("El dominio debe tener la forma mi-tienda.myshopify.com (solo letras minúsculas, números y guiones)."),
      );
      domainRef.current?.focus();
      return;
    }

    setSaving(true);
    setErrorMessage("");
    try {
      const res = await enrollShopifyDirect({
        shopDomain,
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
      });

      if (res && "error" in res) {
        const status = res.error?.statusCode ?? res.error?.status;
        // Server text wins except on 503, whose server-config text is not
        // actionable for the merchant; the fallbacks cover bodies without a
        // usable message (empty, HTML, or a bare status phrase).
        const serverMessage = apiBodyMessage(res.error);

        if (status === 409) {
          setErrorMessage(
            serverMessage ?? t("Esta tienda ya está vinculada a otra cuenta de Beartrack."),
          );
        } else if (status === 422) {
          // El backend ya intentó acuñar un token con estas credenciales: si
          // llegamos acá, Shopify las rechazó. Su mensaje manda porque es el
          // único que sabe la causa exacta.
          setErrorMessage(
            serverMessage ??
              t("Shopify rechazó estas credenciales. Revisa que la app esté instalada en la tienda."),
          );
        } else if (status === 503) {
          setErrorMessage(
            t("La integración con Shopify no está disponible en este momento. Contacta a soporte."),
          );
        } else {
          if (!serverMessage) console.error(res.error);
          setErrorMessage(serverMessage ?? t("No se pudo vincular la tienda. Intenta nuevamente."));
        }
        return;
      }

      const data = res?.data ?? {};
      if (data.webhooksRegistered === false) {
        // La tienda quedó vinculada, pero sin webhooks no llegan los pedidos:
        // decirlo explícitamente en vez de mostrar un éxito liso.
        toast.warning(
          t("Tienda vinculada, pero no se pudieron activar los avisos de pedidos. Contacta a soporte."),
        );
      } else {
        toast.success(t("Tienda Shopify vinculada correctamente"));
      }

      onClose();
      if (typeof onSuccess === "function") onSuccess();
    } catch (error) {
      console.error(error?.response || error);
      setErrorMessage(t("No se pudo vincular la tienda. Intenta nuevamente."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={t("Vincular Shopify")}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            disabled={saving || !canSubmit}
          >
            {saving ? t("Vinculando...") : t("Vincular tienda")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="mig-logo" aria-hidden="true">
            <img src={iconList.shopify} alt="" className="mig-logo__img dark-safe-img" />
          </span>
          <p className="text-sm text-fg-muted">
            {t(
              "Crea una app en tu panel de Shopify y pega aquí sus credenciales. Los pedidos se crearán automáticamente en BearTrack.",
            )}
          </p>
        </div>

        <ol className="list-decimal space-y-2 rounded-lg border border-border bg-bg-muted px-5 py-4 text-sm text-fg-muted">
          <li>
            {t("En tu panel de Shopify, entra a")}{" "}
            <strong className="font-semibold text-fg">
              {t("Configuración → Apps y canales de venta → Desarrollar apps")}
            </strong>
            .
          </li>
          <li>
            {t("Elige")}{" "}
            <strong className="font-semibold text-fg">{t("Crear app en Dev Dashboard")}</strong>{" "}
            {t("y ponle un nombre, por ejemplo")}{" "}
            <code className="rounded bg-bg-subtle px-1 py-0.5 font-mono text-fg">BearTrack</code>.
          </li>
          <li>
            {t("En la pestaña de versiones, agrega estos permisos y publica la versión:")}
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {SHOPIFY_SCOPES.map((scope) => (
                <code
                  key={scope}
                  className="rounded bg-bg-subtle px-1.5 py-0.5 font-mono text-xs text-fg"
                >
                  {scope}
                </code>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-fg-subtle">
              {t(
                "Los cinco son necesarios. Sin write_fulfillments, Shopify nunca marca los pedidos como entregados; sin read_products, los pedidos llegan sin identificar de que producto se trata.",
              )}
            </p>
          </li>
          <li>
            <strong className="font-semibold text-fg">{t("Instala la app")}</strong>{" "}
            {t("en tu tienda.")}
          </li>
          <li>
            {t("Abre los ajustes de la app y copia el")}{" "}
            <strong className="font-semibold text-fg">Client ID</strong> {t("y el")}{" "}
            <strong className="font-semibold text-fg">Client secret</strong>.
          </li>
        </ol>

        <div className="space-y-1.5">
          <FieldLabel htmlFor={domainId}>{t("Dominio de tu tienda")}</FieldLabel>
          <div
            className={cn(
              "flex overflow-hidden rounded-lg border bg-bg transition-colors focus-within:ring-2 focus-within:ring-brand-500",
              errorMessage ? "border-status-error" : "border-border",
            )}
          >
            <span
              className="flex select-none items-center border-r border-border bg-bg-muted px-3 text-sm text-fg-muted"
              aria-hidden="true"
            >
              https://
            </span>
            <input
              id={domainId}
              ref={domainRef}
              type="text"
              className="h-10 min-w-0 flex-1 bg-transparent px-3 font-mono text-sm text-fg placeholder:text-fg-subtle focus:outline-none"
              placeholder="mi-tienda.myshopify.com"
              value={domain}
              onChange={(e) => {
                setErrorMessage("");
                setDomain(e.target.value);
              }}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={errorMessage ? true : undefined}
              aria-describedby={errorMessage ? errorId : undefined}
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <FieldLabel htmlFor={clientIdId}>Client ID</FieldLabel>
            <input
              id={clientIdId}
              type="text"
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 font-mono text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500"
              value={clientId}
              onChange={(e) => {
                setErrorMessage("");
                setClientId(e.target.value);
              }}
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <div className="space-y-1.5">
            <FieldLabel htmlFor={clientSecretId}>Client secret</FieldLabel>
            <input
              id={clientSecretId}
              // type="password": este valor da control total sobre la Admin API
              // de la tienda del comercio y no debe quedar visible en pantalla.
              type="password"
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 font-mono text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500"
              value={clientSecret}
              onChange={(e) => {
                setErrorMessage("");
                setClientSecret(e.target.value);
              }}
              onKeyDown={(e) => e.key === "Enter" && canSubmit && !saving && handleSubmit()}
              autoComplete="new-password"
              spellCheck={false}
            />
          </div>
        </div>

        <p id={errorId} aria-live="polite" className="flex min-h-[1rem] items-start gap-1 text-xs">
          {errorMessage ? (
            <span className="flex items-start gap-1 text-status-error">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {errorMessage}
            </span>
          ) : (
            <span className="text-fg-muted">
              {t("Tus credenciales se guardan cifradas y no vuelven a mostrarse.")}
            </span>
          )}
        </p>

        <a
          href="https://help.shopify.com/es/manual/apps/app-types/custom-apps"
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1.5 text-sm text-brand-600 hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          {t("Ver la guía de Shopify")}
        </a>
      </div>
    </Modal>
  );
}
